/**
 * Marketing Terminal — Landing Pages
 * Master spec §2.7. Archetype-driven workflow.
 */

import React, { useState } from 'react';
import { Globe, Layers, Plus, Rocket, X, ExternalLink, Edit3 } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface Archetype { id: string; name: string; description: string; use_case: string; section_pattern: string[]; }
interface LandingPage {
  id: string; slug: string; title: string; status: string;
  archetype_id: string; archetype_name?: string; primary_persona_id: string | null;
  persona_name?: string; deployment_url: string | null; deployed_at: string | null;
  updated_at: string;
}
interface Persona { id: string; name: string; state: string; }

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });

export function LandingPagesPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <Inner />
    </QueryClientProvider>
  );
}

function Inner() {
  const [showCreate, setShowCreate] = useState(false);
  const { data: pages } = useQuery<{ data: LandingPage[] }>({
    queryKey: ['marketing-landing-pages'],
    queryFn: () => adminFetch('/api/admin/marketing/landing-pages'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>
            Landing Pages
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Archetype-driven landing pages. Pick a starting point, target a persona, deploy to coachcedar.com/lp/&lt;slug&gt;.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1 rounded px-3 py-1.5 text-xs font-medium text-white"
          style={{ backgroundColor: 'var(--cedar-primary)' }}
        >
          <Plus className="h-3 w-3" /> New landing page
        </button>
      </div>

      {showCreate && <CreateForm onClose={() => setShowCreate(false)} />}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {pages?.data?.map(p => <PageRow key={p.id} page={p} />)}
        {pages?.data?.length === 0 && (
          <div className="lg:col-span-2 rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
            <Globe className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            No landing pages yet. Click "New landing page" to start from an archetype.
          </div>
        )}
      </div>
    </div>
  );
}

function PageRow({ page }: { page: LandingPage }) {
  const queryClient = useQueryClient();
  const deploy = useMutation({
    mutationFn: () => adminFetch(`/api/admin/marketing/landing-pages/${page.id}/deploy`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-landing-pages'] }),
  });

  const statusColor = page.status === 'deployed' ? 'var(--cedar-primary)' : page.status === 'ready' ? 'var(--status-warning)' : 'var(--text-tertiary)';

  return (
    <div className="rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900">{page.title}</span>
            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase" style={{ backgroundColor: statusColor + '20', color: statusColor }}>
              {page.status}
            </span>
          </div>
          <code className="text-[10px] text-gray-500">/lp/{page.slug}</code>
          {page.archetype_name && (
            <div className="mt-1 text-[11px] text-gray-500">
              <Layers className="mr-0.5 inline h-2.5 w-2.5" />
              {page.archetype_name}{page.persona_name && ` · ${page.persona_name}`}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        {page.deployment_url ? (
          <a href={page.deployment_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            <ExternalLink className="h-3 w-3" /> {page.deployment_url}
          </a>
        ) : <span className="text-[10px] italic text-gray-400">Not deployed</span>}
        <div className="flex gap-2">
          <button className="rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">
            <Edit3 className="mr-0.5 inline h-3 w-3" /> Edit
          </button>
          <button
            onClick={() => deploy.mutate()}
            disabled={deploy.isPending}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--cedar-primary)' }}
          >
            <Rocket className="h-3 w-3" /> {deploy.isPending ? '...' : page.status === 'deployed' ? 'Re-deploy' : 'Deploy'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [archetypeId, setArchetypeId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [campaignBrief, setCampaignBrief] = useState('');

  const { data: archetypes } = useQuery<{ data: Archetype[] }>({
    queryKey: ['marketing-archetypes'],
    queryFn: () => adminFetch('/api/admin/marketing/landing-archetypes'),
  });
  const { data: personas } = useQuery<{ data: Persona[] }>({
    queryKey: ['marketing-personas-prod'],
    queryFn: () => adminFetch('/api/admin/marketing/personas'),
  });

  const create = useMutation({
    mutationFn: () =>
      adminFetch('/api/admin/marketing/landing-pages', {
        method: 'POST',
        body: JSON.stringify({
          archetype_id: archetypeId,
          primary_persona_id: personaId || null,
          title,
          slug,
          campaign_brief: campaignBrief,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-landing-pages'] });
      onClose();
    },
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Archetype</label>
          <select value={archetypeId} onChange={e => setArchetypeId(e.target.value)} className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs">
            <option value="">— pick a starting point —</option>
            {archetypes?.data?.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Persona</label>
          <select value={personaId} onChange={e => setPersonaId(e.target.value)} className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs">
            <option value="">(no persona)</option>
            {personas?.data?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" placeholder='e.g. "Coach Cedar for runners"' />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Slug</label>
          <input value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs font-mono" placeholder="coachcedar-for-runners" />
        </div>
      </div>
      <textarea value={campaignBrief} onChange={e => setCampaignBrief(e.target.value)} placeholder="Campaign brief (optional, helps Cowork tailor the copy)" className="mt-3 w-full rounded border border-gray-200 px-2 py-1.5 text-xs" rows={3} />
      <div className="mt-3 flex gap-2">
        <button onClick={() => create.mutate()} disabled={!archetypeId || !title || !slug || create.isPending} className="rounded px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50" style={{ backgroundColor: 'var(--cedar-primary)' }}>
          {create.isPending ? '...' : 'Create draft'}
        </button>
        <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500">Cancel</button>
      </div>
    </div>
  );
}

export default LandingPagesPage;
